import { t } from "@/i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpen, Loader2, Moon, Printer, RefreshCw, Square, Volume2 } from "lucide-react";
import { API, api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { playVoice, stopHuey } from "@/lib/voice";
import { useVoiceState } from "@/components/SpeakButton";
import { startLullaby, stopLullaby } from "@/lib/lullaby";

// Sleepy Mode preference only (non-sensitive) — localStorage is appropriate.
const SLEEPY_KEY = "ccs-sleepy";
const isSleepyPref = () => localStorage.getItem(SLEEPY_KEY) !== "0";

// Night theme + lullaby while `active`; cleans up on unmount.
const useSleepyMode = (active) => {
  useEffect(() => {
    if (!active) return;
    document.documentElement.classList.add("sleepy");
    startLullaby();
    return () => { document.documentElement.classList.remove("sleepy"); stopLullaby(); };
  }, [active]);
};

export const printStory = (imageUrl, story, artist) => {
  const w = window.open("", "_blank");
  if (!w) return;
  const d = w.document;
  const el = (tag, text, cls) => { const n = d.createElement(tag); if (text) n.textContent = text; if (cls) n.className = cls; return n; };
  d.title = story.title;
  const style = el("style", "body{font-family:Georgia,serif;color:#1E293B;padding:32px;max-width:720px;margin:0 auto}h1{font-family:Arial,sans-serif;text-align:center;margin:0 0 4px}.by{text-align:center;color:#666;margin:0 0 20px;font-family:Arial,sans-serif}img{display:block;width:60%;margin:0 auto 24px;border:3px solid #1E293B;border-radius:16px}p{font-size:18px;line-height:1.6}.tip{margin-top:24px;padding:12px 16px;border:2px dashed #1E293B;border-radius:12px;font-family:Arial,sans-serif;font-size:14px}@media print{body{padding:0}}");
  d.head.appendChild(style);
  d.body.appendChild(el("h1", story.title));
  d.body.appendChild(el("p", `A bedtime story for ${artist}`, "by"));
  const img = el("img");
  img.onload = () => setTimeout(() => w.print(), 200);
  img.src = imageUrl;
  d.body.appendChild(img);
  story.body.split(/\n+/).filter(Boolean).forEach((para) => d.body.appendChild(el("p", para)));
  if (story.tip) d.body.appendChild(el("div", `Read-aloud tip: ${story.tip}`, "tip"));
};

export const StoryReader = ({ story, imageUrl, artist, audioUrl, streaming, onRewrite, rewriting }) => {
  const voice = useVoiceState();
  const id = `story-${audioUrl}`;
  const loading = voice?.id === id && voice.status === "loading";
  const speaking = voice?.id === id && voice.status === "playing";
  const [sleepy, setSleepy] = useState(isSleepyPref);
  const sleepyActive = sleepy && (loading || speaking);
  useSleepyMode(sleepyActive);
  const toggleSleepy = () => { localStorage.setItem(SLEEPY_KEY, sleepy ? "0" : "1"); setSleepy(!sleepy); };
  useEffect(() => () => stopHuey(), []);
  const toggleSpeak = () => {
    if (loading || speaking) { stopHuey(); return; }
    playVoice(id, { url: audioUrl }, `${story.title}. ${story.body}`);
  };
  return (
    <div className="space-y-4 relative" data-testid="story-reader" data-sleepy={sleepyActive}>
      {sleepyActive && createPortal(<div className="sleepy-sky" aria-hidden data-testid="sleepy-sky" />, document.body)}
      <h3 className="font-display text-2xl font-semibold" data-testid="story-title">{story.title || (streaming ? "Once upon a time..." : "")}</h3>
      <div className="font-serif text-[17px] leading-relaxed space-y-3 text-slate-800" data-testid="story-body">
        {story.body.split(/\n+/).filter(Boolean).map((p, i) => <p key={`${i}-${p.slice(0, 24)}`}>{p}</p>)}
        {streaming && <span className="inline-block w-2 h-5 bg-[#FF5252] animate-pulse align-middle" />}
      </div>
      {story.tip && !streaming && (
        <p className="text-sm rounded-2xl bg-[#FFF9F2] border-2 border-dashed border-[#FFD166] px-4 py-3" data-testid="story-tip"><b>{t("read_aloud_tip")}</b> {story.tip}</p>
      )}
      {!streaming && (
        <div className="flex flex-wrap gap-2 pt-1">
          {audioUrl && (
            <button onClick={toggleSpeak} className={`btn-pop !py-2.5 !px-4 text-sm ${speaking || loading ? "btn-ink" : "btn-red"}`} data-testid="story-read-aloud-button" data-state={loading ? "loading" : speaking ? "playing" : "idle"} aria-pressed={speaking || loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : speaking ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              {loading ? t("story_voice_loading") : speaking ? t("huey_stop_reading") : t("huey_read_aloud")}
            </button>
          )}
          {audioUrl && (
            <button onClick={toggleSleepy} className={`btn-pop !py-2.5 !px-4 text-sm ${sleepy ? "btn-ink" : "btn-white"}`} data-testid="sleepy-mode-button" aria-pressed={sleepy} title={t("sleepy_mode_hint")}>
              <Moon className={`w-4 h-4 ${sleepyActive ? "sleepy-moon text-[#FDE68A]" : ""}`} /> {t("sleepy_mode")}
            </button>
          )}
          {imageUrl && (
            <button onClick={() => printStory(imageUrl, story, artist)} className="btn-pop btn-white !py-2.5 !px-4 text-sm" data-testid="story-print-button">
              <Printer className="w-4 h-4" /> {t("print_story")}
            </button>
          )}
          {onRewrite && (
            <button onClick={onRewrite} disabled={rewriting} className="btn-pop btn-white !py-2.5 !px-4 text-sm" data-testid="story-rewrite-button">
              <RefreshCw className={`w-4 h-4 ${rewriting ? "animate-spin" : ""}`} /> {t("new_story")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const EMPTY = { title: "", body: "", tip: "" };

export const StoryDialog = ({ art, artist, onClose, onSaved }) => {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef(null);

  useEffect(() => {
    if (!art) return;
    setStory(null); setError(""); setStreaming(false);
    setLoading(true);
    api.get(`/artworks/${art.id}/story`).then((r) => setStory(r.data)).catch(() => setStory(null)).finally(() => setLoading(false));
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the artwork changes
  }, [art]);

  const write = useCallback(async () => {
    setError(""); setStreaming(true); setStory({ ...EMPTY });
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let raw = "";
    try {
      const res = await fetch(`${API}/artworks/${art.id}/story`, { method: "POST", credentials: "include", signal: ctrl.signal });
      if (!res.ok) throw new Error("Request failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.delta) {
            raw += ev.delta;
            const [first, ...rest] = raw.split("\n");
            setStory({ title: raw.includes("\n") ? first.replace(/^["'*#\s]+|["'*#\s]+$/g, "") : "", body: rest.join("\n").replace(/read-aloud tip:.*$/is, "").trim(), tip: "" });
          } else if (ev.done) {
            setStory(ev.story);
            onSaved?.(art.id);
          } else if (ev.error) {
            setError(ev.error);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setError("The storyteller is taking a nap — please try again in a moment.");
    } finally {
      setStreaming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API/EMPTY are module constants
  }, [art, onSaved]);

  return (
    <Dialog open={!!art} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="story-dialog">
        {art && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><BookOpen className="w-6 h-6 text-[#FF5252]" /> {t("story_time")}</DialogTitle>
              <DialogDescription>A gentle bedtime story about "{art.page_title}", written by AI just for {artist}. Kind, calm and read-aloud friendly.</DialogDescription>
            </DialogHeader>
            {loading ? (
              <p className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="w-5 h-5 animate-spin" /> {t("checking_the_bookshelf")}</p>
            ) : error ? (
              <div className="space-y-3 py-4">
                <p className="font-bold text-red-600" data-testid="story-error">{error}</p>
                <button onClick={write} className="btn-pop btn-red !py-2.5 !px-4 text-sm" data-testid="story-retry-button">{t("try_again")}</button>
              </div>
            ) : !story ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-20 h-20 mx-auto rounded-3xl bg-[#FFD166] border-2 border-[#1E293B] flex items-center justify-center shadow-[3px_4px_0_0_#1E293B]"><BookOpen className="w-10 h-10" /></div>
                <p className="text-slate-600">{t("no_story_on_the_shelf_yet_shall_we_write")}</p>
                <button onClick={write} className="btn-pop btn-red" data-testid="story-write-button">{t("write_my_story")}</button>
              </div>
            ) : (
              <StoryReader story={story} imageUrl={`${API}/artworks/${art.id}/image`} audioUrl={`${API}/artworks/${art.id}/story/audio`} artist={artist} streaming={streaming} onRewrite={write} rewriting={streaming} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
